import debounce from 'lodash/debounce';
import merge from 'lodash/merge';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import * as AuthAPI from '@/apis/auth';
import * as CreatorsAPI from '@/apis/creators';
import { getCdnAssetUrl } from '@/assets';
import { env } from '@/env';
import { CUSTOMER_PLAN } from '@/models/auth/customer-plan.enum';
import { BRAND } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useDatadogStore } from '@/stores/datadog';
import { useFlagStore } from '@/stores/flag';
import { useNotificationStore } from '@/stores/notification';
import { useTrackingStore } from '@/stores/tracking';
import { logger } from '@/utils/logger';
import { loadParagon } from '@/utils/paragon-loader';

const airtableIcon = getCdnAssetUrl('integrations/airtable.svg');
const bigqueryIcon = getCdnAssetUrl('integrations/bigquery.svg');
const bitlyIcon = getCdnAssetUrl('integrations/bitly.svg');
const canvaIcon = getCdnAssetUrl('integrations/canva.svg');
const gmailIcon = getCdnAssetUrl('integrations/gmail.svg');
const googleDriveIcon = getCdnAssetUrl('integrations/googleDrive.svg');
const googleSheetsIcon = getCdnAssetUrl('integrations/googleSheets.svg');
const kawoIcon = getCdnAssetUrl('integrations/kawo.svg');
const klaviyoIcon = getCdnAssetUrl('integrations/klaviyo.svg');
const mailchimpIcon = getCdnAssetUrl('integrations/mailchimp.svg');
const microsoftOutlookIcon = getCdnAssetUrl('integrations/microsoft-outlook.svg');
const rssFeedIcon = getCdnAssetUrl('integrations/rssFeed.svg');
const s3Icon = getCdnAssetUrl('integrations/s3.svg');
const salesforceIcon = getCdnAssetUrl('integrations/salesforce.svg');
const shopifyIcon = getCdnAssetUrl('integrations/shopify.svg');
const zendeskIcon = getCdnAssetUrl('integrations/zendesk.svg');

const INTEGRATION_API_KEY_LABEL = 'dh-integrations';
const ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL = env.integrationsRedirectUrl;
const REAUTHENTICATION_PERIOD_MS = 5 * 60 * 1000; // Five minutes

export const AUTHENTICATION_TYPE = Object.freeze({
  user: 'user',
  brand: 'brand',
});

export const INTEGRATIONS = {
  BRAND: {
    SALESFORCE: {
      name: 'Salesforce',
      value: 'salesforce',
      icon: salesforceIcon,
      description: 'Connect to Salesforce to create cases',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
    },
    ZENDESK: {
      name: 'Zendesk',
      value: 'zendesk',
      icon: zendeskIcon,
      description: 'Connect to Zendesk to create tickets',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
    },
    BIGQUERY: {
      name: 'BigQuery',
      value: 'bigquery',
      icon: bigqueryIcon,
      description: 'Access your most important social metrics from any channel in your BigQuery',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      connectOptions: {
        overrideRedirectUrl: ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL,
      },
      flag: 'integrationBigquery',
    },
    KLAVIYO: {
      name: 'Klaviyo',
      value: 'klaviyo',
      icon: klaviyoIcon,
      description: 'Send your LikeShop email submissions directly to your lists in Klaviyo',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'integrationKlaviyo',
    },
    MAILCHIMP: {
      name: 'Mailchimp',
      value: 'mailchimp',
      icon: mailchimpIcon,
      description: 'Send your LikeShop email submissions directly to your lists in Mailchimp',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'integrationMailchimp',
    },
    GOOGLE_SHEETS: {
      name: 'Google Sheets',
      value: 'googlesheets',
      icon: googleSheetsIcon,
      description:
        'Automatically sync your social media metrics directly from Dash Social to Google Sheets',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      connectOptions: {
        overrideRedirectUrl: ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL,
      },
      flag: 'integrationGoogleSheets',
    },
    GOOGLE_DRIVE: {
      name: 'Google Drive',
      value: 'googledrive',
      icon: googleDriveIcon,
      description:
        'Sync your approved assets live directly from Google Drive to Dash Social’s Library',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      connectOptions: {
        overrideRedirectUrl: ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL,
      },
      flag: 'integrationGoogleDrive',
    },
    AMAZON_S3: {
      name: 'Amazon S3',
      value: 'amazons3',
      icon: s3Icon,
      description: 'Access your most important social metrics from any channel in your AWS s3',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'integrationS3',
    },
    AIRTABLE: {
      name: 'Airtable',
      value: 'airtable',
      icon: airtableIcon,
      description: 'Access your most important social metrics from any channel in your Airtable',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'integrationAirtable',
    },
    RSSFEED: {
      name: 'RSS',
      value: 'custom.rss',
      icon: rssFeedIcon,
      description: 'Add feeds to automate publishing to Facebook and X from your website and blogs',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
    },
    CANVA: {
      name: 'Canva',
      value: 'custom.canva',
      icon: canvaIcon,
      description: 'Export images directly to Canva and keep editing',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'canvaIntegration',
    },
    CANVA_PLATFORM: {
      name: 'Canva Editor',
      value: 'canva_platform',
      icon: canvaIcon,
      description: 'Utilize Canva to edit your creative for publishing',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'canvaEdit',
    },
    SHOPIFY: {
      name: 'Shopify',
      value: 'shopify',
      icon: shopifyIcon,
      description: 'Connect to Shopify to send product directly to your Creators',
      permission: BRAND.CREATORS.CAN_ACCESS_CREATORS,
      flag: 'creatorGifting',
    },
    BITLY: {
      name: 'Bitly',
      value: 'custom.bitly',
      icon: bitlyIcon,
      description: 'Connect to Bitly to shorten your links and track your traffic',
      permission: BRAND.CREATORS.CAN_ACCESS_CREATORS,
      flag: 'affiliateLinks',
    },
    KAWO: {
      name: 'KAWO',
      value: 'custom.kawo',
      icon: kawoIcon,
      description: 'Access your KAWO Dashboard',
      plans: [
        CUSTOMER_PLAN.ADVANCE,
        CUSTOMER_PLAN.ADVANCE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ADVANCE_PREMIUM,
        CUSTOMER_PLAN.ADVANCE_PREMIUM_AND_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE,
        CUSTOMER_PLAN.ENTERPRISE_SOCIAL_LISTENING,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM,
        CUSTOMER_PLAN.ENTERPRISE_PREMIUM_AND_SOCIAL_LISTENING,
      ],
      permission: BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS,
      flag: 'integrationKawo',
    },
  },
  USER: {
    GMAIL: {
      name: 'Gmail',
      value: 'gmail',
      icon: gmailIcon,
      description: 'Connect a gmail account',
      connectOptions: {
        overrideRedirectUrl: ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL,
      },
    },
    OUTLOOK: {
      name: 'Outlook',
      value: 'outlook',
      icon: microsoftOutlookIcon,
      description: 'Connect a Microsoft outlook account',
      connectOptions: {
        overrideRedirectUrl: ENV_SPECIFIC_INTEGRATIONS_OAUTH_URL,
      },
    },
  },
};

export const useIntegrationStore = defineStore('integration', () => {
  const authStore = useAuthStore();
  const notificationStore = useNotificationStore();
  const flagStore = useFlagStore();
  const trackingStore = useTrackingStore();
  const datadogStore = useDatadogStore();

  const initialized = ref(false);
  const paragonIntegrationsUser = ref(null);
  const paragonIntegrationMetadataUser = ref([]);

  const paragonIntegrationsBrand = ref(null);
  const paragonIntegrationMetadataBrand = ref([]);
  const authenticating = ref(false);
  const authenticatedAsBrand = ref(false);
  const authenticatedAsUser = ref(false);
  const pendingAuthentication = ref(Promise.resolve());
  const lastAuthenticationType = ref(null);

  const lastAuthenticatedAt = ref({
    [AUTHENTICATION_TYPE.brand]: null,
    [AUTHENTICATION_TYPE.user]: null,
  });

  const lastAuthenticatedBrandId = ref(null);

  function extractIntegrationType(type) {
    return (type?.value ?? type)?.toLowerCase();
  }

  const hasAccessToIntegrations = computed(() =>
    authStore.guard(BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS),
  );

  const currentAuthenticationType = computed(
    () =>
      (authenticatedAsUser.value && AUTHENTICATION_TYPE.user) ||
      (authenticatedAsBrand.value && AUTHENTICATION_TYPE.brand) ||
      null,
  );

  const integrations = (authenticationType = AUTHENTICATION_TYPE.user) =>
    computed(() => {
      const isUser = authenticationType === AUTHENTICATION_TYPE.user;
      const paragonIntegrations = isUser
        ? paragonIntegrationsUser.value
        : paragonIntegrationsBrand.value;
      const paragonMetadata = isUser
        ? paragonIntegrationMetadataUser.value
        : paragonIntegrationMetadataBrand.value;
      return Object.keys(paragonIntegrations ?? {}).reduce((acc, integrationType) => {
        const paragonMeta = paragonMetadata?.find((metadata) => metadata.type === integrationType);
        const dhMetadata = Object.values(INTEGRATIONS)
          .flatMap((integrationCategory) => Object.values(integrationCategory))
          .find((integration) => integration.value === integrationType);

        const isIntegrationActive = !!paragonMeta;
        const hasFlag = dhMetadata && (!dhMetadata.flag || flagStore.flags[dhMetadata.flag]);
        const hasPlan = !dhMetadata?.plans || authStore.hasPlan(dhMetadata?.plans);
        const hasPermission = !dhMetadata?.permission || authStore.guard(dhMetadata?.permission);

        if (isIntegrationActive && hasFlag && hasPlan && hasPermission) {
          acc[integrationType] = paragonIntegrations[integrationType] ?? {};
          acc[integrationType].metadata = paragonMeta ?? {};
          acc[integrationType].metadata.description = dhMetadata?.description;
        }

        return acc;
      }, {});
    });

  const integrationList = (authenticationType = AUTHENTICATION_TYPE.user) =>
    computed(() => Object.values(integrations(authenticationType).value));

  const integrationIcon = computed(() => (type) => {
    const value = extractIntegrationType(type);
    return (
      Object.values(INTEGRATIONS.BRAND).find((i) => i.value === value)?.icon ||
      Object.values(INTEGRATIONS.USER).find((i) => i.value === value)?.icon
    );
  });

  const isConnected = computed(() => (integrationValue) => {
    const value = extractIntegrationType(integrationValue);
    const authType = Object.values(INTEGRATIONS.USER).some((i) => i.value === value)
      ? AUTHENTICATION_TYPE.user
      : AUTHENTICATION_TYPE.brand;

    if (authType === AUTHENTICATION_TYPE.user) {
      return !!paragonIntegrationsUser.value?.[value]?.enabled;
    }
    return !!paragonIntegrationsBrand.value?.[value]?.enabled;
  });

  const getWorkflow = computed(() => (integrationValue, workflowId) => {
    const value = extractIntegrationType(integrationValue);
    const authType = Object.values(INTEGRATIONS.USER).some((i) => i.value === value)
      ? AUTHENTICATION_TYPE.user
      : AUTHENTICATION_TYPE.brand;

    if (authType === AUTHENTICATION_TYPE.user) {
      return paragonIntegrationsUser.value?.[value]?.configuredWorkflows?.[workflowId];
    }
    return paragonIntegrationsBrand.value?.[value]?.configuredWorkflows?.[workflowId];
  });

  const hasWorkflow = computed(
    () => (integrationValue, workflowId) => !!getWorkflow.value(integrationValue, workflowId),
  );

  const isWorkflowEnabled = computed(
    () => (integrationValue, workflowId) =>
      !!getWorkflow.value(integrationValue, workflowId)?.enabled,
  );

  function integrationName(integrationValue) {
    const value = extractIntegrationType(integrationValue);
    return (
      Object.values(INTEGRATIONS.BRAND).find((i) => i.value === value)?.name ||
      Object.values(INTEGRATIONS.USER).find((i) => i.value === value)?.name
    );
  }

  function integrationConnectOptions(integrationValue) {
    const value = extractIntegrationType(integrationValue);
    return (
      Object.values(INTEGRATIONS.BRAND).find((i) => i.value === value)?.connectOptions ||
      Object.values(INTEGRATIONS.USER).find((i) => i.value === value)?.connectOptions ||
      {}
    );
  }

  function integrationMixpanelProps(integration) {
    const type = extractIntegrationType(integration);
    const name = integrationName(integration);
    return {
      integrationType: type,
      integrationName: name,
    };
  }

  async function deleteOldIntegrationApiKeys() {
    try {
      const response = await authStore.getApiKeysForBrand({
        brandId: authStore.currentBrand.id,
      });
      const payload = response?.data ?? [];
      if (Array.isArray(payload)) {
        const deleteRequests = payload
          .filter((apiKey) => apiKey.label === INTEGRATION_API_KEY_LABEL)
          .map((apiKey) =>
            AuthAPI.deleteApiKeyForBrand({
              brandId: authStore.currentBrand?.id,
              apiKeyId: apiKey?.apiKeyId,
            }),
          );
        await Promise.all(deleteRequests);
      }
    } catch (error) {
      logger.error('Failed to delete old integration api key', {}, error);
      throw error;
    }
  }

  async function updateDHApiKey() {
    try {
      await deleteOldIntegrationApiKeys();
    } catch {
      // ignoring errors here so we still create new api key
    }
    const response = await authStore.generateApiKey({
      label: INTEGRATION_API_KEY_LABEL,
      brandId: authStore.currentBrand?.id,
    });
    const payload = response?.data;
    const token = payload?.token;
    const paragon = await loadParagon();
    await paragon.setUserMetadata({
      api_key: token,
    });
  }

  async function hasValidApiKey(user) {
    const apiKey = user?.meta?.api_key;
    if (!apiKey) {
      return false;
    }
    try {
      await AuthAPI.validateApiKey({ token: apiKey });
    } catch {
      return false;
    }
    return true;
  }

  async function updateDHApiKeyIfRequired() {
    const paragon = await loadParagon();
    const user = paragon.getUser();
    if (user?.meta) {
      if (!(await hasValidApiKey(user))) {
        await updateDHApiKey();
      }
    }
  }

  async function loadIntegrations(authenticationType) {
    const paragon = await loadParagon();
    const user = paragon.getUser();
    if (authenticationType === AUTHENTICATION_TYPE.user) {
      paragonIntegrationsUser.value = user?.integrations;
      paragonIntegrationMetadataUser.value = paragon.getIntegrationMetadata();
    } else {
      paragonIntegrationsBrand.value = user?.integrations;
      paragonIntegrationMetadataBrand.value = paragon.getIntegrationMetadata();
    }
  }

  async function attemptAuthentication(authenticationType) {
    const brand = authStore.currentBrand;

    if (!brand) {
      throw new Error(
        'No current brand is set in the auth store, so cannot authenticate with Paragon!',
      );
    }

    // To avoid spamming auth and Paragon, we should avoid trying to re-authenticate
    // with the same type of authentication too often.

    const lastAuthTime = lastAuthenticatedAt.value[authenticationType];
    if (
      lastAuthTime &&
      new Date().getTime() - lastAuthTime.getTime() < REAUTHENTICATION_PERIOD_MS &&
      (authenticationType === AUTHENTICATION_TYPE.user ||
        lastAuthenticatedBrandId.value === brand.id) &&
      lastAuthenticationType.value === authenticationType // Only skip if type matches
    ) {
      return;
    }
    localStorage.removeItem('paragon-connect-user-state');

    const paragonProjectId =
      authenticationType === AUTHENTICATION_TYPE.user
        ? env.paragonUserProjectId
        : env.paragonBrandProjectId;

    if (!paragonProjectId) {
      throw new Error(`Paragon ${authenticationType} Project ID is missing.`);
    }

    if (!authStore.identity && authenticationType === AUTHENTICATION_TYPE.user) {
      throw new Error(
        'No identity is set in the auth store, so cannot authenticate as a user with Paragon!',
      );
    }

    // Defensive check for required identity properties to prevent undefined access
    if (authenticationType === AUTHENTICATION_TYPE.user && authStore.identity) {
      if (
        !authStore.identity.first_name ||
        !authStore.identity.last_name ||
        !authStore.identity.email
      ) {
        throw new Error(
          'User identity is missing required properties (first_name, last_name, email) for Paragon authentication!',
        );
      }
    }

    const name =
      authenticationType === AUTHENTICATION_TYPE.brand
        ? brand.name
        : `${authStore.identity.first_name} ${authStore.identity.last_name} (${authStore.identity.email})`;

    authenticating.value = true;
    let paragon;
    try {
      const response =
        authenticationType === AUTHENTICATION_TYPE.user
          ? await AuthAPI.getUserIntegrations()
          : await AuthAPI.getBrandIntegrations({ brandId: brand.id });
      const paragonJwtToken = response.data.paragonJwtToken;

      paragon = await loadParagon();
      await paragon.authenticate(paragonProjectId, paragonJwtToken);
      await paragon.setUserMetadata({ name, brand_id: brand.id });
      // API keys are only needed at the brand level.
      if (authenticationType === AUTHENTICATION_TYPE.brand) {
        await updateDHApiKeyIfRequired();
      }

      await loadIntegrations(authenticationType);
    } catch (error) {
      if (authenticationType === AUTHENTICATION_TYPE.user) {
        paragonIntegrationsUser.value = null;
        paragonIntegrationMetadataUser.value = [];
      } else {
        paragonIntegrationsBrand.value = null;
        paragonIntegrationMetadataBrand.value = [];
      }

      const errorContext = {
        authenticationType,
        brandId: brand?.id,
        brandName: brand?.name,
        hasIdentity: !!authStore.identity,
        identityId: authStore.identity?.id,
        errorMessage: error?.message,
        isFetchError: error?.message === 'Failed to fetch',
      };

      logger.error('Failed paragon authentication', errorContext, error);

      if (error?.message === 'Failed to fetch') {
        throw new Error('Failed to authenticate with Paragon: Request error');
      }

      throw new Error(`Failed to authenticate with Paragon: ${error?.message || 'Unknown error'}`);
    } finally {
      authenticating.value = false;

      const authenticated = paragon?.getUser()?.authenticated ?? false;
      if (authenticationType === AUTHENTICATION_TYPE.user) {
        authenticatedAsUser.value = authenticated;
      } else {
        authenticatedAsBrand.value = authenticated;
        lastAuthenticatedBrandId.value = authenticated ? brand.id : null;
      }

      lastAuthenticatedAt.value[authenticationType] = new Date();
      initialized.value = true;
      lastAuthenticationType.value = authenticationType;
    }
  }

  async function authenticate(authenticationType) {
    // If we're authenticating, we should clear the previous state.
    if (!Object.values(AUTHENTICATION_TYPE).includes(authenticationType)) {
      throw new Error(`${authenticationType} is not a valid authentication type!`);
    }

    pendingAuthentication.value = pendingAuthentication.value
      .catch((error) => {
        logger.warn('Previous authentication attempt failed', {}, error);
      })
      .then(() => attemptAuthentication(authenticationType));
    await pendingAuthentication.value;
  }

  /**
   * Send a proxy request to an integration's API via Paragon, automatically reauthenticating
   * with Paragon if we receive a 401 or 403 error when reauthenticateOnForbidden is true.
   *
   * @param integration The integration to use.
   * @param path The integration API path.
   * @param params Parameters sent to Paragon SDK, such as the method or body.
   * @param reauthenticateOnForbidden If true, reauthenticate on 401 or 403 status.
   * @returns {Promise<Object>}
   */
  async function request(
    integration,
    path,
    params = { method: 'GET' },
    reauthenticateOnForbidden = true,
  ) {
    try {
      const paragon = await loadParagon();
      return await paragon.request(integration, path, params);
    } catch (e) {
      // Note: code 7303 indicates the downstream service responded to Paragon with a 401. Due
      // to a bug on Paragon's end, of which they have been informed, the response status from
      // Paragon in this scenario is not always 401.
      const isErrorAuthenticationRelated =
        [401, 403].includes(e?.response?.status) || e?.response?.code === '7303';
      if (isErrorAuthenticationRelated && reauthenticateOnForbidden) {
        await authenticate(currentAuthenticationType.value);
        return request(integration, path, params, false);
      }
      e.request = { path, params };
      throw e;
    }
  }

  function logout() {
    localStorage.removeItem('paragon-connect-user-state');
    authenticatedAsBrand.value = false;
    authenticatedAsUser.value = false;
    lastAuthenticatedAt.value = {
      [AUTHENTICATION_TYPE.brand]: null,
      [AUTHENTICATION_TYPE.user]: null,
    };
    lastAuthenticatedBrandId.value = null;
  }

  async function disconnect(integrationValue, authenticationType = AUTHENTICATION_TYPE.user) {
    if (!integrationValue) {
      return;
    }

    const value = extractIntegrationType(integrationValue);

    const isUserIntegration = Object.values(INTEGRATIONS.USER).some((i) => i.value === value);
    const authType =
      authenticationType ||
      (isUserIntegration ? AUTHENTICATION_TYPE.user : AUTHENTICATION_TYPE.brand);

    await authenticate(authType);

    const integration = integrationList(authType).value.find((i) => i.metadata.type === value);
    const credentialId = integration ? integration.credentialId : null;

    const paragon = await loadParagon();
    await paragon.uninstallIntegration(value, credentialId);
    await loadIntegrations(authType);
  }

  async function connect(integrationValue, onCloseCallback = () => {}) {
    const value = extractIntegrationType(integrationValue);
    const name = integrationName(integrationValue);
    const connectOptions = integrationConnectOptions(integrationValue);
    const mixpanelProps = integrationMixpanelProps(integrationValue);
    trackingStore.track('Started Integration Connection', mixpanelProps);

    const isUserIntegration = Object.values(INTEGRATIONS.USER).some((i) => i.value === value);
    const authType = isUserIntegration ? AUTHENTICATION_TYPE.user : AUTHENTICATION_TYPE.brand;

    const onSuccessCallback = debounce(
      () => {
        trackingStore.track('Integration Connected', {
          integrationType: value,
          integrationName: name,
        });
        notificationStore.setToast({
          message: `Successfully connected to ${name}`,
        });
        loadIntegrations(authType);

        if (value === INTEGRATIONS.BRAND.SHOPIFY.value) {
          CreatorsAPI.connectShopifyAccount({
            brandId: lastAuthenticatedBrandId.value,
          });
        }
      },
      3000,
      { leading: true, trailing: false },
    );

    await authenticate(authType);

    const paragon = await loadParagon();
    paragon.connect(
      value,
      merge(
        {
          onSuccess: onSuccessCallback,
          onUninstall: async () => {
            trackingStore.track('Integration Disconnected', mixpanelProps);
            await loadIntegrations(authType);

            if (value === INTEGRATIONS.BRAND.SHOPIFY.value) {
              CreatorsAPI.disconnectShopifyAccount({
                brandId: lastAuthenticatedBrandId.value,
              });
            }
          },
          onWorkflowChange: async () => {
            trackingStore.track('Integration Workflow Change', mixpanelProps);
            await loadIntegrations(authType);
          },
          onError: async (error) => {
            trackingStore.track('Integration Connection Failure', {
              ...mixpanelProps,
              errorType: error,
            });
            notificationStore.setToast({
              message: `There was a problem connecting your ${name} account.  Please try again.`,
              type: 'error',
            });

            const userCancelledEmailOauth =
              ['gmail', 'outlook'].includes(value) &&
              ['7100', '7110', '41001', '7109'].includes(error.code);

            if (!userCancelledEmailOauth && isUserIntegration && authenticatedAsUser.value) {
              const context = {
                action: 'connectEmail',
                provider: name,
              };
              datadogStore.addError(error, context);
            }

            await loadIntegrations(authType);
          },
          onClose: () => {
            onCloseCallback();
          },
        },
        connectOptions,
      ),
    );
  }

  async function runWorkflow(workflowId, payload) {
    if (!authenticatedAsBrand.value) {
      await authenticate(AUTHENTICATION_TYPE.brand);
    }
    const paragon = await loadParagon();
    return paragon?.workflow(workflowId, payload);
  }

  async function instanceUrl(integration) {
    const integrationType = extractIntegrationType(integration);
    const paragon = await loadParagon();
    const user = paragon.getUser();
    const providerData = user?.integrations?.[integrationType]?.providerData;
    return providerData?.instanceUrl ?? '';
  }

  async function ensureAuthenticated() {
    if (!authenticatedAsUser.value) {
      await authenticate(AUTHENTICATION_TYPE.user);
    }
  }

  async function completeInstall(integration, integrationOptions) {
    // If we're not authenticated as a brand, authenticate as a brand.
    if (!authenticatedAsBrand.value) {
      await authenticate(AUTHENTICATION_TYPE.brand);
    }

    // Complete the integration install.
    const paragon = await loadParagon();
    await paragon.completeInstall(integration, integrationOptions);

    // Track the integration connection.
    const name = integrationName(integration);
    trackingStore.track('Integration Connected', {
      integrationType: integration,
      integrationName: name,
    });

    // Show a success toast.
    notificationStore.setToast({
      message: `Successfully connected to ${name}`,
      type: 'success',
    });

    // Load brand integrations.
    await loadIntegrations(AUTHENTICATION_TYPE.brand);

    // Connect the Shopify account if the integration is Shopify.
    if (integration === INTEGRATIONS.BRAND.SHOPIFY.value) {
      CreatorsAPI.connectShopifyAccount({
        brandId: lastAuthenticatedBrandId.value,
      });
    }
  }

  watch(
    () => authStore.currentBrand,
    async () => {
      if (!authenticatedAsUser.value) {
        await authenticate(AUTHENTICATION_TYPE.brand);
      }
    },
  );

  watch(
    () => authStore.isLoggedIn,
    async (newIsLoggedIn, oldIsLoggedIn) => {
      if (newIsLoggedIn === oldIsLoggedIn) {
        return;
      }
      if (!newIsLoggedIn) {
        logout();
      }
    },
    { immediate: true },
  );

  return {
    hasAccessToIntegrations,
    initialized,
    paragonIntegrationsUser,
    paragonIntegrationMetadataUser,
    paragonIntegrationsBrand,
    paragonIntegrationMetadataBrand,
    authenticating,
    integrationList,
    integrations,
    integrationIcon,
    authenticate,
    authenticatedAsBrand,
    authenticatedAsUser,
    ensureAuthenticated,
    connect,
    disconnect,
    isConnected,
    getWorkflow,
    hasWorkflow,
    isWorkflowEnabled,
    integrationName,
    runWorkflow,
    instanceUrl,
    extractIntegrationType,
    integrationMixpanelProps,
    logout,
    request,
    completeInstall,
  };
});
